import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Search, FileSpreadsheet, FileText, MoreHorizontal, Eye, Pencil, Trash2, CheckCircle2, XCircle,
  RotateCcw, ChevronLeft, ChevronRight, ArrowUpDown, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtMoney } from "@/lib/api";
import { exportExcel, exportPDF } from "@/lib/export";
import { OptionSelect, RefSelect, clearOptionsCache } from "./FieldInput";
import { ResourceForm } from "./ResourceForm";
import { MotifDialog, DetailSheet, displayValue } from "./Dialogs";
import { StatusBadge } from "./StatusBadge";

const WF_STATUTS = [
  { value: "brouillon", label: "Brouillon" }, { value: "valide", label: "Validé" }, { value: "annule", label: "Annulé" },
];

function useColumns(cfg, res, fixedValues) {
  return useMemo(() => {
    const cols = cfg.fields
      .filter((f) => f.list !== false && !(f.name in fixedValues))
      .map((f) => ({ key: f.name, label: f.label, field: f, raw: (r) => rawValue(f, r) }));
    if (cfg.workflow) cols.push({ key: "statut", label: "Statut", badge: true, raw: (r) => r.statut });
    if (res === "dettes") cols.push({ key: "etat", label: "Règlement", badge: true, raw: (r) => r.etat });
    if (res === "mouvements") cols.push({ key: "statut", label: "Statut", badge: true, raw: (r) => r.statut });
    return cols;
  }, [cfg, res, fixedValues]);
}

function rawValue(f, r) {
  if (f.type === "number") return r[f.name] == null ? "" : Number(r[f.name]);
  return displayValue(f, r);
}

function Filters({ cfg, filters, setFilters }) {
  const set = (k, v) => setFilters((s) => ({ ...s, [k]: v }));
  return (
    <>
      {cfg.fields.filter((f) => f.filter && f.type !== "text").map((f) => (
        <div key={f.name} className="w-full sm:w-44">
          {f.type === "select" ? (
            <OptionSelect options={f.options} value={filters[f.name]} allowAll onChange={(v) => set(f.name, v)} testId={`filter-${f.name}`} />
          ) : (
            <RefSelect field={f} value={filters[f.name]} allowAll placeholder={f.label} onChange={(v) => set(f.name, v)} testId={`filter-${f.name}`} />
          )}
        </div>
      ))}
      {(cfg.extra_filters || []).map((x) => (
        <div key={x.name} className="w-full sm:w-40">
          <OptionSelect options={x.options} value={filters[x.name]} allowAll onChange={(v) => set(x.name, v)} testId={`filter-${x.name}`} />
        </div>
      ))}
      {cfg.workflow && (
        <div className="w-full sm:w-36">
          <OptionSelect options={WF_STATUTS} value={filters.statut} allowAll onChange={(v) => set("statut", v)} testId="filter-statut" />
        </div>
      )}
      {cfg.date_field && (
        <div className="flex gap-2 items-center">
          <Input type="date" className="h-9 w-[150px]" value={filters.date_from || ""} onChange={(e) => set("date_from", e.target.value)} data-testid="filter-date-from" />
          <span className="text-xs text-muted-foreground">au</span>
          <Input type="date" className="h-9 w-[150px]" value={filters.date_to || ""} onChange={(e) => set("date_to", e.target.value)} data-testid="filter-date-to" />
        </div>
      )}
    </>
  );
}

function Cell({ col, row }) {
  if (col.badge) return <StatusBadge value={row[col.key]} />;
  const f = col.field;
  if (f.name === "statut" && f.type === "select") return <StatusBadge value={row.statut} />;
  const v = displayValue(f, row);
  if (f.type === "number") {
    const neg = Number(row[f.name]) < 0;
    return <span className={`font-mono ${neg ? "text-primary" : ""}`}>{v}</span>;
  }
  return <span className="truncate block max-w-[240px]" title={typeof v === "string" ? v : ""}>{v}</span>;
}

function RowMenu({ row, cfg, res, can, onAction, extraActions }) {
  const wf = cfg.workflow;
  const statutOk = !cfg.editable_statuts || cfg.editable_statuts.includes(row.statut);
  const editable = !cfg.readonly && can(cfg.module, cfg.write_action || "modifier") && (!wf || row.statut === "brouillon") && statutOk;
  const extras = (extraActions?.(row) || []).filter((a) => a.show !== false);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`row-actions-${row.id}`}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem onClick={() => onAction("view", row)} data-testid="action-view"><Eye className="h-4 w-4 mr-2" />Détails & historique</DropdownMenuItem>
        {editable && <DropdownMenuItem onClick={() => onAction("edit", row)} data-testid="action-edit"><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>}
        {wf && row.statut === "brouillon" && can(cfg.module, "valider") && (
          <DropdownMenuItem onClick={() => onAction("validate", row)} data-testid="action-validate"><CheckCircle2 className="h-4 w-4 mr-2 text-emerald-600" />{cfg.validate_label || "Valider"}</DropdownMenuItem>
        )}
        {wf && row.statut === "valide" && can(cfg.module, "annuler") && can(cfg.module, "creer") && (
          <DropdownMenuItem onClick={() => onAction("correct", row)} data-testid="action-correct"><RotateCcw className="h-4 w-4 mr-2" />Corriger</DropdownMenuItem>
        )}
        {wf && row.statut !== "annule" && can(cfg.module, "annuler") && (
          <DropdownMenuItem onClick={() => onAction("cancel", row)} data-testid="action-cancel"><XCircle className="h-4 w-4 mr-2 text-primary" />Annuler</DropdownMenuItem>
        )}
        {extras.map((a) => (
          <DropdownMenuItem key={a.key} onClick={() => a.onClick(row)} data-testid={`action-${a.key}`}>
            {a.icon && <a.icon className="h-4 w-4 mr-2" />}{a.label}
          </DropdownMenuItem>
        ))}
        {!cfg.readonly && can(cfg.module, cfg.write_action || "supprimer") && (!wf || row.statut === "brouillon") && statutOk && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onAction("delete", row)} className="text-primary" data-testid="action-delete"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const NO_FIXED = {};

export function ResourceTable({ res, fixedValues = NO_FIXED, extraActions, detailExtra, reloadKey = 0, onChanged }) {
  const { meta, can } = useAuth();
  const cfg = meta.resources[res];
  const cols = useColumns(cfg, res, fixedValues);
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [sort, setSort] = useState({ key: null, dir: "desc" });
  const [data, setData] = useState({ items: [], total: 0, totals: {} });
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState({ type: null, row: null });

  useEffect(() => {
    const t = setTimeout(() => setDq(q), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    setPage(1);
  }, [dq, filters, size]);

  const params = useCallback((extra = {}) => {
    const p = { ...filters, ...fixedValues, q: dq, page, size, ...extra };
    if (sort.key) Object.assign(p, { sort: sort.key, dir: sort.dir });
    return Object.fromEntries(Object.entries(p).filter(([, v]) => v !== null && v !== undefined && v !== ""));
  }, [filters, fixedValues, dq, page, size, sort]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/r/${res}`, { params: params() });
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [res, params]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const changed = () => {
    load();
    clearOptionsCache();
    onChanged?.();
  };

  const doExport = async (kind) => {
    try {
      const r = await api.get(`/r/${res}`, { params: params({ page: 1, size: 10000 }) });
      const sub = filters.date_from || filters.date_to ? `Période : ${filters.date_from || "…"} → ${filters.date_to || "…"}` : "";
      if (kind === "xlsx") exportExcel(cfg.label, cols, r.data.items);
      else exportPDF(cfg.label, cols, r.data.items, { company: meta.settings?.nom_entreprise, subtitle: sub, footer: meta.settings?.pied_de_page_rapports });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      changed();
    } catch (e) {
      toast.error(errMsg(e));
      throw e;
    }
  };

  const onAction = (type, row) => {
    if (type === "validate") {
      run(() => api.post(`/r/${res}/${row.id}/valider`), `${row.numero || "Élément"} ${cfg.validate_label === "Payer" ? "payé" : "validé"}`).catch(() => {});
      return;
    }
    setDlg({ type, row });
  };
  const close = () => setDlg({ type: null, row: null });
  const pages = Math.max(1, Math.ceil(data.total / size));
  const toggleSort = (k) => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "desc" ? "asc" : "desc" }));

  return (
    <div className="bg-card border rounded-lg" data-testid={`${res}-table`}>
      <div className="p-3 sm:p-4 border-b flex flex-wrap gap-2 items-center">
        <div className="relative w-full sm:w-64">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
          <Input className="pl-9 h-9" placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${res}-search-input`} />
        </div>
        <Filters cfg={cfg} filters={filters} setFilters={setFilters} />
        <div className="flex gap-2 ml-auto">
          {can(cfg.module, "exporter") && (
            <>
              <Button variant="outline" size="sm" onClick={() => doExport("xlsx")} data-testid={`${res}-export-excel`}><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
              <Button variant="outline" size="sm" onClick={() => doExport("pdf")} data-testid={`${res}-export-pdf`}><FileText className="h-4 w-4 mr-1" />PDF</Button>
            </>
          )}
          {!cfg.readonly && can(cfg.module, cfg.write_action || "creer") && (
            <Button size="sm" onClick={() => setDlg({ type: "create" })} data-testid={`${res}-create-button`}><Plus className="h-4 w-4 mr-1" />Nouveau</Button>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>
              {cols.map((c) => (
                <th key={c.key} className={`px-3 py-2.5 font-semibold whitespace-nowrap ${c.field?.type === "number" ? "text-right" : "text-left"}`}>
                  <button className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => !c.field?.computed && toggleSort(c.key)}>
                    {c.label}{!c.field?.computed && <ArrowUpDown className="h-3 w-3 opacity-40" />}
                  </button>
                </th>
              ))}
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={r.id} className={`border-t hover:bg-accent/40 transition-colors ${["annule", "annulee", "rejetee"].includes(r.statut) ? "opacity-55" : ""}`} data-testid={`${res}-row`}>
                {cols.map((c) => (
                  <td key={c.key} className={`px-3 py-2 whitespace-nowrap ${c.field?.type === "number" ? "text-right" : ""}`}>
                    <Cell col={c} row={r} />
                    {c.key === "quantite" && r.alerte && <AlertTriangle className="inline h-3.5 w-3.5 ml-1 text-amber-600" />}
                  </td>
                ))}
                <td className="px-2 text-right">
                  <RowMenu row={r} cfg={cfg} res={res} can={can} onAction={onAction} extraActions={extraActions && ((row) => extraActions(row, changed))} />
                </td>
              </tr>
            ))}
            {!loading && !data.items.length && (
              <tr><td colSpan={cols.length + 1} className="py-14 text-center text-muted-foreground text-sm" data-testid={`${res}-empty`}>Aucun enregistrement</td></tr>
            )}
            {loading && !data.items.length && (
              <tr><td colSpan={cols.length + 1} className="py-14 text-center text-muted-foreground text-sm">Chargement…</td></tr>
            )}
          </tbody>
          {Object.keys(data.totals || {}).length > 0 && data.items.length > 0 && (
            <tfoot className="border-t-2 border-foreground/80 bg-muted/40 font-semibold" data-testid={`${res}-totals`}>
              <tr>
                {cols.map((c, i) => (
                  <td key={c.key} className={`px-3 py-2 whitespace-nowrap ${c.field?.type === "number" ? "text-right font-mono" : ""}`}>
                    {i === 0 ? "Total (hors annulés)" : c.key in data.totals ? (c.field.money ? fmtMoney(data.totals[c.key]) : data.totals[c.key]) : ""}
                  </td>
                ))}
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-t text-xs text-muted-foreground">
        <span data-testid={`${res}-count`}>{data.total} enregistrement(s)</span>
        <div className="flex items-center gap-2">
          <select className="h-8 border rounded px-2 bg-background" value={size} onChange={(e) => setSize(Number(e.target.value))} data-testid="page-size-select">
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
          </select>
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="page-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span>Page {page} / {pages}</span>
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="page-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <ResourceForm res={res} cfg={cfg} open={dlg.type === "create" || dlg.type === "edit"} onOpenChange={(o) => !o && close()}
        item={dlg.type === "edit" ? dlg.row : null} fixedValues={fixedValues} onSaved={changed} />
      <DetailSheet open={dlg.type === "view"} onOpenChange={(o) => !o && close()} item={dlg.row} cfg={cfg} res={res}
        extra={dlg.type === "view" && detailExtra ? detailExtra(dlg.row) : null} />
      <MotifDialog open={dlg.type === "cancel"} onOpenChange={(o) => !o && close()} title={`Annuler ${dlg.row?.numero || cfg.singular}`}
        description={dlg.row?.statut === "valide" ? "Les écritures liées seront contre-passées. L'opération reste visible dans l'historique." : "Le brouillon sera marqué comme annulé."}
        confirmLabel="Confirmer l'annulation" onConfirm={(m) => run(() => api.post(`/r/${res}/${dlg.row.id}/annuler`, { motif: m }), "Annulation enregistrée")} />
      <MotifDialog open={dlg.type === "correct"} onOpenChange={(o) => !o && close()} title={`Corriger ${dlg.row?.numero || ""}`}
        description="L'opération validée sera annulée (contre-passation) et une copie en brouillon sera créée pour correction."
        confirmLabel="Annuler et dupliquer" onConfirm={(m) => run(() => api.post(`/r/${res}/${dlg.row.id}/corriger`, { motif: m }), "Copie de correction créée en brouillon")} />
      <AlertDialog open={dlg.type === "delete"} onOpenChange={(o) => !o && close()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet enregistrement ?</AlertDialogTitle>
            <AlertDialogDescription>La suppression est tracée dans le journal d'audit.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Retour</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-button" onClick={() => run(() => api.delete(`/r/${res}/${dlg.row.id}`), "Supprimé").catch(() => {})}>Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
